/**
 * streakMilestones.ts — welke streakstanden we vieren.
 *
 * WAAROM DIT EEN EIGEN BESTAND IS
 *
 * De lijst stond in `StreakMilestoneBurst.tsx`, en de sessieafsluiting had er een
 * eigen kopie van als inline literal. Die twee waren uiteengelopen: de burst
 * vierde bij **60**, de samenvatting bij **50**.
 *
 * Voor een kind is dat niet "een inconsistentie" maar een gebroken belofte: bij
 * streak 50 juicht de samenvatting en gebeurt er in de sessie niets, en bij 60
 * precies omgekeerd. Beide keren lijkt de app kapot, en beide keren is er niets
 * fout gegaan behalve dat twee bestanden een ander getal bijhielden.
 *
 * De lijst leeft nu in een puur bestand zonder React-import, zodat elke
 * aanroeper — component, hook, of een bewaking in het adminportaal — hem kan
 * lezen zonder een component binnen te halen.
 */

/** Streakstanden waarop we vieren. Oplopend; de eerste die je kruist, geldt. */
export const STREAK_MILESTONES = [3, 7, 14, 30, 60, 100] as const;

/**
 * Geeft de mijlpaal terug die net gekruist is, of null.
 *
 * Pure functie: aanroepers detecteren de overgang zelf (met refs in een effect)
 * en gebruiken de uitkomst om de viering te tonen.
 */
export function getCrossedMilestone(prevStreak: number, currentStreak: number): number | null {
  if (currentStreak <= prevStreak) return null;
  for (const milestone of STREAK_MILESTONES) {
    if (prevStreak < milestone && currentStreak >= milestone) {
      return milestone;
    }
  }
  return null;
}

/** Is deze stand exact een mijlpaal? Gebruikt door de sessieafsluiting. */
export function isStreakMilestone(streak: number): boolean {
  return (STREAK_MILESTONES as readonly number[]).includes(streak);
}
